import AxiosInstance from '@/api/axios';
import { getClientId } from '@/utils/helper';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export type FeedbackStatusFilter = 'all' | 'liked' | 'disliked';

export interface IFeedbackItem {
  _id: string;
  status: string;
  feedback: string;
  expectedResponse: string;
  human_message: string;
  ai_message: string;
  senderName?: string;
  senderEmail?: string;
  senderPhone?: string;
  created_date: string;
  /** history.sender — used when no visitor name exists. */
  sender: string;
  messageId: string;
  created_at: string | null;
  branch_id: string | null;
  region: string | null;
  /** messageId points at a deleted history document. */
  orphaned?: boolean;
}

export interface IFeedbackBucket {
  liked: number;
  disliked: number;
  total: number;
  positivePercent: number | null;
}

export interface IFeedbackStats {
  overall: IFeedbackBucket;
  byBranch: Record<string, IFeedbackBucket>;
  byRegion: Record<string, IFeedbackBucket>;
}

interface IFetchParams {
  branch?: string | null;
  region?: string | null;
  status?: FeedbackStatusFilter;
  page?: number;
  perPage?: number;
}

// access.middleware 403s non-OWNER roles unless a branch or region is sent.
const scopeParams = (branch?: string | null, region?: string | null) => {
  const params: Record<string, string> = {};
  const client = getClientId();
  if (client) params.organization_id = client;
  if (branch && branch !== 'all' && branch !== 'others') {
    params.branch_id = branch;
  } else if (region && region !== 'all') {
    params.region_id = region;
  }
  return params;
};

export const fetchFeedbacks = createAsyncThunk(
  'queryFeedback/fetchFeedbacks',
  async (
    { branch, region, status = 'all', page = 1, perPage = 10 }: IFetchParams,
    thunkAPI
  ) => {
    try {
      const response = await AxiosInstance.get('/query-feedback', {
        params: {
          ...scopeParams(branch, region),
          ...(status !== 'all' && { status }),
          page,
          perPage,
          sort: 'dislikedFirst',
        },
      });

      const { data, pagination } = response.data.data;
      return {
        items: data as IFeedbackItem[],
        page: pagination.currentPage as number,
        totalPages: pagination.totalPages as number,
        total: pagination.totalRecords as number,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const fetchFeedbackStats = createAsyncThunk(
  'queryFeedback/fetchStats',
  async ({ branch, region }: IFetchParams, thunkAPI) => {
    try {
      const response = await AxiosInstance.get('/query-feedback/stats', {
        params: scopeParams(branch, region),
      });
      return response.data.data as IFeedbackStats;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

interface IQueryFeedbackState {
  items: IFeedbackItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  total: number;
  stats: IFeedbackStats | null;
  statsLoading: boolean;
}

const initialState: IQueryFeedbackState = {
  items: [],
  loading: false,
  loadingMore: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  total: 0,
  stats: null,
  statsLoading: false,
};

const queryFeedbackSlice = createSlice({
  name: 'queryFeedback',
  initialState,
  reducers: {
    resetFeedbacks: (state) => {
      state.items = [];
      state.currentPage = 1;
      state.totalPages = 1;
      state.total = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeedbacks.pending, (state, action) => {
        const isFirstPage = (action.meta.arg.page ?? 1) === 1;
        if (isFirstPage) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        if (!action.payload) return;

        const { items, page, totalPages, total } = action.payload;

        // Page 1 replaces; later pages append.
        state.items = page === 1 ? items : [...state.items, ...items];
        state.currentPage = page;
        state.totalPages = totalPages;
        state.total = total;
      })
      .addCase(fetchFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = (action.payload as string) ?? 'Failed to load feedback';
      })
      .addCase(fetchFeedbackStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchFeedbackStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload ?? null;
      })
      .addCase(fetchFeedbackStats.rejected, (state) => {
        state.statsLoading = false;
        state.stats = null;
      });
  },
});

export const { resetFeedbacks } = queryFeedbackSlice.actions;
export default queryFeedbackSlice.reducer;
